import React, { useMemo, useState } from 'react';

import { Grid, IconButton, Typography } from '@mui/material';
import type {
  GridColDef,
  GridRenderCellParams,
  GridSortModel,
  GridValidRowModel,
} from '@mui/x-data-grid-premium';
import {
  DataGridPremium,
  gridRowsLookupSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium';
import {
  AlineCheckbox,
  AlineTooltip,
  FiltersToolbarButton,
  getStringOperatorsWithSafeIsAnyOf,
  ListEmpty,
  MergeDialog,
  minWidthTitleCellPx,
  NameDialog,
  NameDialogVariant,
  ReportFiltersPanel,
  RolesOptionsMenu,
  SearchInput,
  Spacer,
  useListToolbarStyles,
} from 'src/components';
import { useAccessGuard } from 'src/components/auth';
import { MergeIcon } from 'src/components/icons/MergeIcon';
import { NewRoleButton } from 'src/components/roles';
import { Apollo } from 'src/gql/generated';
import {
  createDataGridPremiumFooterlessPaperStyles,
  FiltersAnchorProvider,
  useDataGridSearch,
  useFiltersAnchorState,
  useGetRequesterUserDefinedRoles,
  useGetSelectedRowIds,
  useMergeRoles,
} from 'src/hooks';
import { displayOnHover } from 'src/theme';
import { makeStyles } from 'tss-react/mui';
import * as Yup from 'yup';

const useStyles = makeStyles()(() => ({
  grid: {
    height: '100%',
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
  },
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
    position: 'relative',
  },
  xGrid: {
    '& .MuiDataGrid-row': {
      [`&:hover .${displayOnHover}`]: {
        opacity: 1,
        pointerEvents: 'auto',
      },
      '&:hover #overflow-menu': {
        visibility: 'visible',
      },
    },
    flexGrow: 1,
    minHeight: 0,
  },
}));

interface RoleRow extends Pick<Apollo.UserDefinedRole, 'id' | 'roleName' | '__typename'> {
  author: Pick<Apollo.User, 'displayName' | '__typename'> | null;
}
type RoleGridDef = GridColDef<RoleRow>;

export function RolesTab(): JSX.Element {
  const { classes } = useStyles();
  const { anchorEl: filtersAnchorEl, setAnchorEl: setFiltersAnchorEl } = useFiltersAnchorState();
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'roleName' satisfies keyof RoleRow, sort: 'asc' },
  ]);
  const { loading, userDefinedRoles } = useGetRequesterUserDefinedRoles({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const columnDefs: RoleGridDef[] = useMemo(() => {
    return [
      {
        field: 'roleName',
        filterOperators: getStringOperatorsWithSafeIsAnyOf(),
        filterable: true,
        headerName: 'Role Name',
        minWidth: minWidthTitleCellPx,
        renderCell: RoleNameCell,
        type: 'string',
      },
      {
        field: 'author',
        flex: 1,
        headerName: 'Author',
        renderCell: AuthorCell,
        sortable: false,
      },
    ];
  }, []);

  return (
    <FiltersAnchorProvider setAnchorEl={setFiltersAnchorEl}>
      <div className={classes.listContainer}>
        <DataGridPremium
          checkboxSelection
          disableColumnMenu
          disableColumnResize
          disableRowSelectionOnClick
          hideFooter
          showToolbar
          className={classes.xGrid}
          columns={columnDefs}
          loading={loading}
          rows={userDefinedRoles}
          slotProps={{
            filterPanel: {
              filterableColumns: 'visible',
            },
            loadingOverlay: {
              noRowsVariant: 'linear-progress',
              variant: 'linear-progress',
            },
            panel: {
              placement: 'bottom-start',
              target: filtersAnchorEl ?? undefined,
            },
          }}
          slots={{
            baseCheckbox: AlineCheckbox,
            filterPanel: ReportFiltersPanel,
            noRowsOverlay: RolesListEmpty,
            toolbar: RolesListToolbar,
          }}
          sortModel={sortModel}
          sx={createDataGridPremiumFooterlessPaperStyles}
          onSortModelChange={setSortModel}
        />
      </div>
    </FiltersAnchorProvider>
  );
}

function RolesListEmpty(): JSX.Element {
  return <ListEmpty subtitle="Click new to create a role." title="No Roles" />;
}

const useCellStyles = makeStyles()(_theme => ({
  optionsContainer: {
    alignItems: 'center',
    // v9 Grid (was Grid2) doesn't default container to width:100%; without
    // this `justifyContent: space-between` has no extra space to distribute
    // and the menu button collapses next to the role name.
    width: '100%',
  },
}));

function RoleNameCell({ row: { roleName } }: GridRenderCellParams): JSX.Element {
  return <Typography noWrap>{roleName}</Typography>;
}

function AuthorCell({ row }: GridRenderCellParams): JSX.Element {
  const { classes } = useCellStyles();
  const roleSchema = useGetRoleSchema();
  const { author, id: roleId, roleName } = row as RoleRow;
  const [updateRole] = Apollo.useUpdateUserDefinedRoleMutation();
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);

  function handleCloseRenameDialog(): void {
    setRenameDialogOpen(false);
  }

  async function onFormikSubmit(roleName: string): Promise<void> {
    await updateRole({ variables: { input: { props: { roleName }, roleId } } });
    handleCloseRenameDialog();
  }

  return (
    <Grid container className={classes.optionsContainer} sx={{ justifyContent: 'space-between' }}>
      <Typography>{author?.displayName ?? ''}</Typography>
      <RolesOptionsMenu setRenameDialogOpen={setRenameDialogOpen} />
      <NameDialog
        handleClose={handleCloseRenameDialog}
        initialName={roleName}
        open={renameDialogOpen}
        placeholder="Enter role..."
        validationSchema={roleSchema}
        variant={NameDialogVariant.RenameUserDefinedRole}
        onFormikSubmit={onFormikSubmit}
      />
    </Grid>
  );
}

function RolesListToolbar(): JSX.Element {
  const apiRef = useGridApiContext();
  const { classes } = useListToolbarStyles();
  const { mergeRoles } = useMergeRoles();
  const { search, setSearch } = useDataGridSearch();
  const { selectedRowIds } = useGetSelectedRowIds();
  const [mergeDialogOpen, setMergeDialogOpen] = useState<boolean>(false);
  const gridRowsLookup = useGridSelector(apiRef, gridRowsLookupSelector);
  const userDefinedRoles = Object.values(gridRowsLookup).filter(getIsRoleRow);
  const showMergeButton = Boolean(selectedRowIds.length);
  const handleOpenMergeDialog = useAccessGuard({
    callback: () => setMergeDialogOpen(!mergeDialogOpen),
    minRole: Apollo.OrgRole.Admin,
  });

  async function handleMergeRoles(targetRoleId: UUID, sourceRoleIds: UUID[]) {
    return mergeRoles({ variables: { input: { sourceRoleIds, targetRoleId } } });
  }

  return (
    <div className={classes.toolbar}>
      <FiltersToolbarButton />
      <Spacer />
      {showMergeButton && (
        <AlineTooltip arrow placement="top" title="Merge roles">
          <div>
            <IconButton size="large" onClick={handleOpenMergeDialog}>
              <MergeIcon />
            </IconButton>
          </div>
        </AlineTooltip>
      )}
      <SearchInput placeholder="Search roles" search={search} setSearch={setSearch} />
      <NewRoleButton />
      <MergeDialog
        entities={userDefinedRoles}
        getDisabledReason={targetId => {
          return !targetId ? 'Select a target role' : '';
        }}
        handleMerge={handleMergeRoles}
        message={`Are you sure you want to merge? All selected roles will be deleted and merged into the target role. This action cannot be undone.`}
        open={mergeDialogOpen}
        selectedIds={selectedRowIds}
        title="Merge roles?"
        onClose={() => setMergeDialogOpen(false)}
      />
    </div>
  );
}

export function useGetRoleSchema() {
  const { userDefinedRoles } = useGetRequesterUserDefinedRoles();
  const lowerCaseRoleNames = userDefinedRoles.map(r => r.roleName.toLowerCase());

  return useMemo(() => {
    return Yup.object({
      name: Yup.string()
        .required('Required')
        .test('unique-validation', 'Role name must be unique', newRoleName => {
          const lowerCaseNewRoleName = newRoleName.toLowerCase();
          const allLowercaseNames = [lowerCaseNewRoleName, ...lowerCaseRoleNames];
          const nameCount = allLowercaseNames.filter(
            roleName => roleName === lowerCaseNewRoleName
          ).length;
          return nameCount <= 1;
        }),
    });
  }, [lowerCaseRoleNames]);
}

function getIsRoleRow(row: GridValidRowModel): row is RoleRow {
  return (row as RoleRow).__typename === 'UserDefinedRole';
}
